import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Flex, Spinner, Text } from '@chakra-ui/react';

/**
 * ProtectedRoute component for handling role-based access control
 * 
 * @param {Object} props - Component props
 * @param {Array} props.allowedRoles - Array of roles allowed to access the route
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @returns {React.ReactNode} - Rendered component
 */
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Flex 
        height="100vh" 
        width="100%" 
        justifyContent="center" 
        alignItems="center" 
        direction="column"
      >
        <Spinner 
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="primary.500"
          size="xl"
          mb={4}
        />
        <Text>Verifying authentication...</Text>
      </Flex>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has the required role
  const hasRequiredRole = allowedRoles.includes(currentUser.role);
  
  if (!hasRequiredRole) {
    // Redirect based on user role
    if (currentUser.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (currentUser.role === 'committee') {
      return <Navigate to="/committee/dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;
